import deepmerge from "deepmerge";
import { cloneDeep, set } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";

import { getValue, setStyles } from "@intelligems/ig-plugin/core";
import {
  AnyProgressBarWidget,
  AnyProgressBarWidgetDeviceConfig,
  AnyProgressBarWidgetLikeObject,
  AnyProgressBarWidgetTextReplacements,
  AnyProgressBarWidgetVariables,
  AnyProgressBarWidgetWindowState,
  DiscountProgressBarProgress,
  DiscountUnitType,
  DottedJoin,
  ExperienceCurrencyDto,
  MemCartItem,
  OfferDiscountLikeObject,
  OfferDto,
  TiersUnlockedReplacements,
  WidgetDto,
} from "@intelligems/ig-types";
import {
  AnyProgressBarWidgetBase,
  buildRewardSnippet,
  calculateMessages,
  calculateProgressBarPercentage,
  getTooltipMessage,
  getWhatsUnlocked,
  setAnyProgressBarVariables,
  standardTiersUnlockedReplacements,
} from "@intelligems/ig-widgets";

import { useConfiguration } from "../../../api/configurations/use-configuration";
import { DiscountStateStub, DiscountStub } from "../stubs";

export interface IgDiscountProgressBarProps {
  configType: "mobile" | "desktop";
  progressBarWidget: AnyProgressBarWidget;
  threshold?: number;
  discountState?: OfferDto;
  userInterfaceVariables?: AnyProgressBarWidgetVariables;
}

export const IgDiscountProgressBar = ({
  configType,
  progressBarWidget,
  threshold,
  discountState,
  userInterfaceVariables,
}: IgDiscountProgressBarProps) => {
  const currencyFormat: ExperienceCurrencyDto =
    useConfiguration("currency_format")?.value;
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const DiscountState = discountState || DiscountStateStub;

  const progressAfterDiscount =
    DiscountState?.tiers?.some((t) => t.isFreeShipping) ?? false;

  const formatter = new Intl.NumberFormat(
    storeCurrencyConfig?.value?.locale ?? "en",
    {
      style: "currency",
      currency:
        currencyFormat?.code || storeCurrencyConfig?.value?.code || "USD",
      maximumFractionDigits: 0,
    }
  );

  const priceCurrencyFormatter = useCallback(
    (price: number | string): string => {
      if (typeof price === "string") {
        return price;
      }
      return formatter.format(price);
    },
    []
  );

  const currencyFormatter = (
    input: number,
    unitType: DiscountUnitType
  ): string => {
    if (unitType === "dollar") {
      return formatter.format(input);
    }
    return input.toString();
  };

  const ProgressBarWidget = useMemo(() => {
    const resp = cloneDeep(progressBarWidget);

    if (!resp.config.desktop?.variables?.textReplacements) {
      set(resp, "config.desktop.variables.textReplacements", {});
    }

    if (userInterfaceVariables) {
      resp.config.desktop!.variables!.textReplacements = deepmerge(
        resp.config.desktop?.variables?.textReplacements || {},
        userInterfaceVariables.textReplacements || {}
      );
    }

    resp.config.desktop!.variables = setAnyProgressBarVariables(
      resp.config.desktop!.variables!
    );

    return resp;
  }, [
    progressBarWidget,
    progressBarWidget?.config?.desktop?.variables?.textReplacements,
    userInterfaceVariables?.textReplacements,
  ]);

  const DiscountProgressBarWidgetStub =
    useCallback((): AnyProgressBarWidgetLikeObject => {
      const state: WidgetDto = {
        id: v4(),
        enabledSitewide: false,
        name: "Discount Progress Bar",
        widgetType: "discountProgressBar",
        config: {
          desktop: {
            variables: { textReplacements: {} },
          },
        },
      };

      return {
        ...state,
        buildRewardSnippet: buildRewardSnippet,
        async calculateMessages(
          discount: OfferDiscountLikeObject,
          cartItems: MemCartItem[],
          subtotalPrice?: number
        ) {
          return calculateMessages(
            discount,
            cartItems,
            true,
            priceCurrencyFormatter,
            undefined,
            subtotalPrice,
            false
          );
        },
        async calculateProgressBarPercentage(
          discount: OfferDiscountLikeObject,
          cartItems: MemCartItem[]
        ) {
          return calculateProgressBarPercentage(
            discount,
            cartItems,
            true,
            undefined,
            progressAfterDiscount
          );
        },
        async getTooltipMessage(
          discount: OfferDiscountLikeObject | null,
          tierId?: string,
          textReplacements?: AnyProgressBarWidgetTextReplacements
        ) {
          return getTooltipMessage(
            discount,
            tierId,
            ProgressBarWidget.config.desktop?.variables?.textReplacements
          );
        },
        getWhatsUnlocked: getWhatsUnlocked,
        setStyles(): void {},
        async standardTiersUnlockedReplacements(
          text: string,
          options: TiersUnlockedReplacements
        ) {
          return standardTiersUnlockedReplacements(
            text,
            options,
            priceCurrencyFormatter
          );
        },
        toModel(): WidgetDto {
          return state;
        },
      };
    }, [ProgressBarWidget]);

  const [windowState, setWindowState] =
    useState<AnyProgressBarWidgetWindowState>(() => {
      return {
        discount: DiscountStub(DiscountState),
        widget: DiscountProgressBarWidgetStub(),
        cartItems: [],
        progress: {
          section: 0,
          progress: 0,
        },
        unlockedMessage: undefined,
        message: undefined,
        tooltipMessage: "",
      };
    });

  const isMobile = "mobile" === configType;
  const prefix = `ig-pb-${progressBarWidget.id}`;

  const handleMessage = useCallback((e: MessageEvent) => {
    return undefined;
  }, []);

  const getWidgetConfig = useCallback(
    (path: DottedJoin<AnyProgressBarWidgetDeviceConfig>) => {
      let value;
      if (isMobile) {
        value = getValue(ProgressBarWidget.config.mobile, path as string);
      }

      if (!value) {
        value = getValue(ProgressBarWidget.config.desktop, path as string);
      }
      return value;
    },
    [ProgressBarWidget?.config, isMobile]
  );

  useEffect(() => {
    setStyles(
      prefix,
      ProgressBarWidget?.config?.desktop?.styles,
      ProgressBarWidget?.config?.mobile?.styles
    );
  }, [
    ProgressBarWidget?.config?.desktop?.styles,
    ProgressBarWidget?.config?.mobile?.styles,
    prefix,
  ]);
  useEffect(() => {
    if (
      DiscountState &&
      threshold !== undefined &&
      ProgressBarWidget.config.desktop &&
      ProgressBarWidget.config.desktop.variables
    ) {
      const fetchNewMessage = async () => {
        const cartItems = [
          {
            isVdProduct: true,
            line: "0",
            productId: "a",
            variantId: "b",
            quantity: DiscountState?.unitType === "dollar" ? 1 : threshold,
            price: DiscountState?.unitType === "dollar" ? threshold : 1,
            final_price: DiscountState?.unitType === "dollar" ? threshold : 1,
            calculatedUnitDollarDiscount: 0,
            cartMessage: "Cart Message",
            discounted_price:
              DiscountState?.unitType === "dollar" ? threshold : 1,
            properties: {
              _igCampaigns: "123",
            },
          },
        ] as MemCartItem[];
        let progressBarPercentage: DiscountProgressBarProgress = {
          section: 0,
          progress: 70,
        };
        await calculateProgressBarPercentage(
          DiscountState as unknown as OfferDiscountLikeObject,
          cartItems,
          true,
          threshold,
          progressAfterDiscount
        ).then((progressBarPercentageResp) => {
          progressBarPercentage = progressBarPercentageResp;
        });
        let messages: { message: string; unlockedMessage: string } = {
          message: "",
          unlockedMessage: "",
        };
        let tooltipMessage = "";
        await calculateMessages(
          DiscountState as unknown as OfferDiscountLikeObject,
          cartItems,
          true,
          priceCurrencyFormatter,
          ProgressBarWidget.config.desktop?.variables?.textReplacements,
          threshold,
          false
        ).then((messageResp) => {
          messages = messageResp;
        });
        await getTooltipMessage(
          DiscountState as unknown as OfferDiscountLikeObject,
          undefined,
          ProgressBarWidget.config.desktop?.variables?.textReplacements
        ).then((resp) => {
          tooltipMessage = resp;
        });
        setWindowState((draft) => {
          const updates = {
            discount: DiscountStub(DiscountState),
            message: messages.message,
            unlockedMessage: messages.unlockedMessage,
            cartItems,
            progress: progressBarPercentage,
            tooltipMessage,
          };

          if (!discountState) {
            updates["progress"] = {
              section: 2,
              progress: 70,
            };
          }

          return {
            ...draft,
            ...updates,
          };
        });
      };
      void fetchNewMessage();
    }
  }, [threshold, DiscountState, ProgressBarWidget]);

  return (
    <AnyProgressBarWidgetBase
      type={"discount"}
      prefix={prefix}
      windowState={windowState}
      isMobile={isMobile}
      config={ProgressBarWidget.config}
      getWidgetConfig={getWidgetConfig}
      currencyFormatter={currencyFormatter}
      handleMessage={handleMessage}
    />
  );
};
